import { useEffect, useMemo, useState } from "react";
import {
  Copy, Check, Eye, ExternalLink, Trash2, Search, ChevronDown, ChevronUp,
  Gift, Calendar, Image as ImageIcon, Film, X,
} from "lucide-react";
import { api } from "@/lib/api";
import MediaPicker from "@/components/MediaPicker";

/**
 * GiftCardsPanel — onglet admin pour la gestion des bons cadeaux.
 *
 * Vue par défaut : liste compacte type "carnet", chaque fiche pliable
 * (adaptée iPhone). Statuts modifiables inline. Boutons rapides pour
 * copier le code, prévisualiser, ouvrir la version imprimable et
 * marquer les changements de statut.
 */

const STATUSES = [
  { key: "pending",  label: "En attente de paiement", short: "En attente", color: "#B89968" },
  { key: "paid",     label: "Payé",                   short: "Payé",       color: "#5A8F5F" },
];

const statusInfo = (key) => STATUSES.find((s) => s.key === key) || STATUSES[0];

export default function GiftCardsPanel({ site }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [msg, setMsg] = useState("");
  const [copied, setCopied] = useState(null);
  // Config médias globale (persistée dans content.giftMedia)
  const [giftMedia, setGiftMedia] = useState({ video: "", poster: "", printHero: "" });
  const [mediaLib, setMediaLib] = useState([]);
  const [pickerFor, setPickerFor] = useState(null); // { key, accept }
  const [savingMedia, setSavingMedia] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [list, content, lib] = await Promise.all([
        api.listGiftCards(site),
        api.getContent(site).catch(() => ({})),
        api.listMedia(site).catch(() => []),
      ]);
      setCards(list);
      setGiftMedia({
        video: content?.giftMedia?.video || "",
        poster: content?.giftMedia?.poster || "",
        printHero: content?.giftMedia?.printHero || "",
      });
      setMediaLib(lib);
    }
    catch (e) { setMsg("Erreur : " + (e?.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [site]);

  const saveGiftMedia = async (next) => {
    setSavingMedia(true);
    try {
      const content = await api.getContent(site).catch(() => ({}));
      const merged = { ...content, giftMedia: { ...giftMedia, ...next } };
      await api.putContent(site, merged);
      setGiftMedia(merged.giftMedia);
      setMsg("✓ Médias enregistrés");
      setTimeout(() => setMsg(""), 1800);
    } catch (e) {
      setMsg("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setSavingMedia(false); }
  };

  const filtered = useMemo(() => {
    const qq = q.toLowerCase().trim();
    return cards
      .filter((c) => filter === "all" ? true : c.status === filter)
      .filter((c) => !qq ? true : (
        c.code.toLowerCase().includes(qq) ||
        (c.offerant?.firstName || "").toLowerCase().includes(qq) ||
        (c.beneficiary?.firstName || "").toLowerCase().includes(qq) ||
        (c.occasion || "").toLowerCase().includes(qq)
      ));
  }, [cards, q, filter]);

  const counts = useMemo(() => {
    const m = { all: cards.length };
    STATUSES.forEach((s) => { m[s.key] = cards.filter((c) => c.status === s.key).length; });
    return m;
  }, [cards]);

  const doCopy = async (label, text) => {
    try { await navigator.clipboard.writeText(text); }
    catch (_e) { /* ignore */ }
    setCopied(label);
    setTimeout(() => setCopied(null), 1600);
  };

  const patchCard = async (code, patch) => {
    try {
      const updated = await api.patchGiftCard(code, patch);
      setCards((prev) => prev.map((c) => (c.code === code ? updated : c)));
    } catch (e) { setMsg("Erreur : " + (e?.response?.data?.detail || e.message)); }
  };

  const deleteCard = async (code) => {
    if (!window.confirm(`Supprimer définitivement le bon ${code} ?`)) return;
    try {
      await api.delGiftCard(code);
      setCards((prev) => prev.filter((c) => c.code !== code));
    } catch (e) { setMsg("Erreur : " + (e?.response?.data?.detail || e.message)); }
  };

  return (
    <div data-testid="gift-cards-panel">
      {/* En-tête */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <h2 className="font-heading text-2xl font-light text-refuge-text mb-1 flex items-center gap-2">
            <Gift size={20} strokeWidth={1.4} className="text-refuge-ruby" />
            Bons cadeaux
          </h2>
          <p className="font-body text-xs text-refuge-soft italic">
            {cards.length} bon{cards.length > 1 ? "s" : ""} au total ·
            Statut modifiable au clic
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-refuge-border text-xs text-refuge-soft hover:border-refuge-ruby hover:text-refuge-ruby transition-all self-start md:self-auto"
        >
          Rafraîchir
        </button>
      </div>

      {msg && (
        <div className="mb-4 px-4 py-2.5 rounded-lg bg-refuge-ruby/10 border border-refuge-ruby/30 text-sm text-refuge-ruby">
          {msg}
        </div>
      )}

      {/* ===== Médias du bon cadeau (config globale) ===== */}
      <GiftMediaConfig
        media={giftMedia}
        saving={savingMedia}
        onPick={(key, accept) => setPickerFor({ key, accept })}
        onClear={(key) => saveGiftMedia({ [key]: "" })}
      />

      {/* Recherche + filtres */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-refuge-soft" strokeWidth={1.4} />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher (code, prénom, occasion…)"
            data-testid="gift-search"
            className="w-full pl-9 pr-3 py-2 border border-refuge-border rounded-lg text-sm font-body bg-white"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-5">
        <FilterChip label={`Tous · ${counts.all}`} active={filter === "all"} onClick={() => setFilter("all")} />
        {STATUSES.map((s) => (
          <FilterChip
            key={s.key}
            label={`${s.label} · ${counts[s.key] || 0}`}
            color={s.color}
            active={filter === s.key}
            onClick={() => setFilter(s.key)}
          />
        ))}
      </div>

      {/* Liste */}
      {loading ? (
        <div className="text-center py-16 text-refuge-soft italic">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-refuge-border rounded-xl">
          <Gift size={22} className="mx-auto mb-3 text-refuge-soft" strokeWidth={1.3} />
          <p className="font-body text-sm text-refuge-soft italic">
            {cards.length === 0
              ? "Aucun bon cadeau pour le moment."
              : "Aucun bon ne correspond à cette recherche."}
          </p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtered.map((card) => (
            <GiftCardRow
              key={card.code}
              card={card}
              expanded={expanded === card.code}
              onToggle={() => setExpanded(expanded === card.code ? null : card.code)}
              onCopy={doCopy}
              copied={copied}
              onPatch={(patch) => patchCard(card.code, patch)}
              onDelete={() => deleteCard(card.code)}
            />
          ))}
        </div>
      )}

      {/* Media picker modal — partagé pour vidéo, poster, print hero */}
      {pickerFor && (
        <MediaPicker
          uploaded={mediaLib}
          site={site}
          accept={pickerFor.accept}
          onPick={async (filename) => {
            await saveGiftMedia({ [pickerFor.key]: filename });
            setPickerFor(null);
          }}
          onClose={() => setPickerFor(null)}
          onUploaded={async () => { setMediaLib(await api.listMedia(site)); }}
        />
      )}
    </div>
  );
}

// ============================================================
// GiftMediaConfig — encadré discret "Médias du bon cadeau"
// ------------------------------------------------------------
// Affiche 3 slots (vidéo porte, poster, photo A5). Chaque slot
// montre l'aperçu actuel et permet d'en choisir un autre depuis
// la bibliothèque (ou d'uploader un nouveau fichier via le picker).
// ============================================================
function GiftMediaConfig({ media, saving, onPick, onClear }) {
  return (
    <details className="mb-5 rounded-xl border border-refuge-border bg-white overflow-hidden group" open>
      <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between hover:bg-refuge-bg/40 transition-colors">
        <div className="flex items-center gap-2 min-w-0">
          <ImageIcon size={14} strokeWidth={1.6} className="text-refuge-ruby shrink-0" />
          <div className="min-w-0">
            <p className="font-body text-[11px] tracking-[0.22em] uppercase text-refuge-ruby">
              Médias du bon cadeau
            </p>
            <p className="font-body italic text-[11px] text-refuge-soft truncate">
              Configuration globale pour tous les bons — vidéo porte, poster, photo A5
            </p>
          </div>
        </div>
        <span className="text-refuge-soft text-[11px] italic shrink-0 ml-2">
          {saving ? "Enregistrement…" : "Modifier"}
        </span>
      </summary>
      <div className="border-t border-refuge-border/60 p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <MediaSlot
          label="Vidéo de la porte"
          hint="Écran 2 du bon digital"
          value={media.video}
          onPick={() => onPick("video", "video/*")}
          onClear={() => onClear("video")}
          isVideo
          fallback="/media/videos/entree-porte.mp4"
          testid="giftmedia-video"
        />
        <MediaSlot
          label="Poster"
          hint="Image affichée avant la vidéo"
          value={media.poster}
          onPick={() => onPick("poster", "image/*")}
          onClear={() => onClear("poster")}
          fallback="/media/images/entree-porte-poster.jpg"
          testid="giftmedia-poster"
        />
        <MediaSlot
          label="Photo carte imprimable"
          hint="Grande image de la carte A5"
          value={media.printHero}
          onPick={() => onPick("printHero", "image/*")}
          onClear={() => onClear("printHero")}
          fallback="/media/images/experiences/bain-de-fleurs.png"
          testid="giftmedia-print"
        />
      </div>
    </details>
  );
}

function MediaSlot({ label, hint, value, onPick, onClear, isVideo, fallback, testid }) {
  const effective = value || fallback;
  const url = effective.startsWith("http")
    ? effective
    : effective.startsWith("/media/")
      ? effective
      : `${process.env.REACT_APP_BACKEND_URL}/api/media/${encodeURIComponent(effective)}?v=${Date.now()}`;
  const isCustom = !!value;
  return (
    <div
      data-testid={testid}
      className="rounded-lg border border-refuge-border overflow-hidden bg-refuge-bg/40"
    >
      <div className="aspect-video bg-refuge-bg overflow-hidden">
        {isVideo ? (
          <video src={url} className="w-full h-full object-cover" muted playsInline preload="metadata" />
        ) : (
          <img src={url} alt="" loading="lazy" className="w-full h-full object-cover" />
        )}
      </div>
      <div className="px-2.5 py-2">
        <div className="flex items-baseline justify-between gap-2 mb-1">
          <p className="font-body text-[10px] tracking-[0.16em] uppercase text-refuge-text truncate">
            {isVideo ? <Film size={10} strokeWidth={1.6} className="inline mr-1 -mt-0.5" /> : null}
            {label}
          </p>
          {isCustom && (
            <button
              type="button"
              onClick={onClear}
              className="text-refuge-soft hover:text-refuge-ruby transition-colors shrink-0"
              title="Rétablir le média par défaut"
              aria-label="Rétablir le média par défaut"
            >
              <X size={11} />
            </button>
          )}
        </div>
        <p className="font-body italic text-[10px] text-refuge-soft truncate">{hint}</p>
        <button
          type="button"
          onClick={onPick}
          className="mt-2 w-full px-2 py-1.5 rounded-full text-[10px] tracking-[0.2em] uppercase font-body text-refuge-ruby border border-refuge-ruby/40 hover:bg-refuge-ruby hover:text-white transition-all"
        >
          {isCustom ? "Remplacer" : "Choisir…"}
        </button>
      </div>
    </div>
  );
}


function FilterChip({ label, active, color, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "px-3 py-1.5 rounded-full font-body text-xs transition-all border " +
        (active
          ? "text-white border-transparent"
          : "bg-white text-refuge-soft border-refuge-border hover:border-refuge-ruby hover:text-refuge-ruby")
      }
      style={active ? { background: color || "#8B2530" } : {}}
    >
      {label}
    </button>
  );
}

// ============================================================
// Une ligne / fiche pliable
// ============================================================
function GiftCardRow({ card, expanded, onToggle, onCopy, copied, onPatch, onDelete }) {
  const s = statusInfo(card.status);
  const digitalUrl = `${window.location.origin}/bon-cadeau/${card.code}`;

  const be = card.beneficiary || {};
  const of = card.offerant || {};
  const opts = card.options || {};
  const prestations = [
    opts.petitDej && "Petit-déjeuner",
    opts.bain && "Bain de fleurs",
    opts.deco && "Décor",
  ].filter(Boolean).join(" · ");
  const dateFmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }); }
    catch (_) { return iso; }
  };

  return (
    <div
      data-testid={`gift-row-${card.code}`}
      className="bg-white border border-refuge-border rounded-xl overflow-hidden"
      style={{ borderLeft: `3px solid ${s.color}` }}
    >
      {/* Ligne compacte */}
      <div className="flex items-center gap-3 p-3 sm:p-4">
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 flex items-center gap-3 min-w-0 text-left"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <p className="font-mono text-[13px] font-semibold tracking-wider text-refuge-text truncate">
                {card.code}
              </p>
              <span
                className="font-body text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full"
                style={{
                  color: s.color,
                  background: s.color + "18",
                  border: `1px solid ${s.color}44`,
                }}
              >
                {s.label}
              </span>
            </div>
            <p className="font-display text-[13px] text-refuge-soft mt-0.5 truncate">
              <em>{be.firstName || "—"}</em>
              {of.firstName && (
                <> · offert par <em>{of.firstName}</em></>
              )}
              {card.occasion && <> · {card.occasion}</>}
            </p>
          </div>
          <div className="text-right shrink-0 hidden sm:block">
            <p className="font-body text-[10px] text-refuge-soft">
              {dateFmt(card.createdAt)}
            </p>
            {card.estimatedAmount ? (
              <p className="font-display text-sm text-refuge-text">
                {card.estimatedAmount}€
                {card.cityTax ? <span className="text-refuge-soft text-[10px]"> + {card.cityTax}€ tx</span> : null}
              </p>
            ) : null}
          </div>
          {expanded
            ? <ChevronUp size={16} strokeWidth={1.5} className="text-refuge-soft shrink-0" />
            : <ChevronDown size={16} strokeWidth={1.5} className="text-refuge-soft shrink-0" />}
        </button>
      </div>

      {/* Détails déployés */}
      {expanded && (
        <div className="border-t border-refuge-border/60 bg-refuge-bg/40 p-3 sm:p-4 space-y-4">
          {/* Actions rapides — simplifiées V2 */}
          <div className="flex flex-wrap gap-2">
            <ActionBtn
              onClick={() => onCopy("code-" + card.code, card.code)}
              icon={copied === "code-" + card.code ? <Check size={12} /> : <Copy size={12} />}
              label={copied === "code-" + card.code ? "Copié !" : "Copier le code"}
              primary
              testid={`copy-code-${card.code}`}
            />
            <ActionBtn
              onClick={() => onCopy("link-" + card.code, digitalUrl)}
              icon={copied === "link-" + card.code ? <Check size={12} /> : <Copy size={12} />}
              label={copied === "link-" + card.code ? "Copié !" : "Copier le lien digital"}
              testid={`copy-link-${card.code}`}
            />
            <ActionBtn
              onClick={() => {
                const wa = (card.waNumber || "33692793155").replace(/\D/g, "");
                const message = `Bonjour ${be.firstName || ""}, voici votre bon cadeau Curieux Refuge ✨\n\n${digitalUrl}\n\nCode : ${card.code}`;
                window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank", "noopener,noreferrer");
              }}
              icon={<ExternalLink size={12} />}
              label="Partager WhatsApp"
              testid={`whatsapp-${card.code}`}
            />
            <ActionBtn
              onClick={() => window.open(digitalUrl, "_blank", "noopener,noreferrer")}
              icon={<Eye size={12} />}
              label="Prévisualiser"
              testid={`preview-${card.code}`}
            />
          </div>

          {/* Statut — V2 : 2 statuts seulement */}
          <div>
            <p className="font-body text-[10px] tracking-[0.22em] uppercase text-refuge-soft mb-2">Statut</p>
            <div className="flex flex-wrap gap-1.5">
              {STATUSES.map((st) => (
                <button
                  key={st.key}
                  type="button"
                  onClick={() => onPatch({ status: st.key })}
                  data-testid={`status-${card.code}-${st.key}`}
                  className={
                    "px-3 py-1.5 rounded-full text-[11px] font-body transition-all border " +
                    (card.status === st.key
                      ? "text-white border-transparent"
                      : "bg-white text-refuge-soft border-refuge-border hover:border-refuge-ruby hover:text-refuge-ruby")
                  }
                  style={card.status === st.key ? { background: st.color } : {}}
                >
                  {st.label}
                </button>
              ))}
            </div>
          </div>

          {/* Résumé du formulaire */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Field label="Offert par">
              {of.firstName || "—"} {of.lastName || ""}
            </Field>
            <Field label="Bénéficiaire">
              {be.firstName || "—"} {be.lastName || ""}
            </Field>
            <Field label="Occasion">{card.occasion || "—"}</Field>
            <Field label="Formule">
              {card.formule === "weekend" ? "Week-end" : "Semaine"}
            </Field>
            <Field label="Nuits">{card.nights} nuit{card.nights > 1 ? "s" : ""}</Field>
            <Field label="Personnes">{card.travelers}</Field>
            <Field label="Prestations incluses" wide>{prestations || "—"}</Field>
          </div>

          {/* Détail du prix */}
          <div className="pt-3 border-t border-refuge-border/60">
            <p className="font-body text-[10px] tracking-[0.22em] uppercase text-refuge-soft mb-2">
              Prix
            </p>
            <div className="rounded-lg bg-white p-3 space-y-1 text-sm">
              <PriceRow label="Prix (nuitées + prestations)" value={card.estimatedAmount || 0} />
              <PriceRow label="Taxe de séjour" value={card.cityTax || 0} subtle />
              <div className="pt-2 mt-1 border-t border-refuge-border/60 flex justify-between font-body">
                <span className="text-[10px] tracking-[0.24em] uppercase text-refuge-ruby">Total</span>
                <span className="font-heading tabular-nums text-refuge-text text-base">
                  {(card.estimatedAmount || 0) + (card.cityTax || 0)}€
                </span>
              </div>
            </div>
            <p className="font-body italic text-[10px] text-refuge-soft mt-2 px-1">
              Taxe de séjour reversée intégralement à la collectivité.
            </p>
          </div>

          {/* Date de validité + créé le */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Field label="Créé le">{dateFmt(card.createdAt)}</Field>
            <Field label="Valable jusqu'au">
              <div className="flex items-center gap-2">
                <Calendar size={12} className="text-refuge-soft shrink-0" strokeWidth={1.4} />
                <input
                  type="date"
                  value={card.validUntil || ""}
                  onChange={(e) => onPatch({ validUntil: e.target.value })}
                  className="text-sm bg-transparent border-b border-refuge-border py-0.5 focus:outline-none focus:border-refuge-ruby"
                />
              </div>
            </Field>
          </div>

          {/* Suppression */}
          <div className="pt-2 border-t border-refuge-border/60">
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] text-refuge-soft hover:text-refuge-ruby transition-colors"
              data-testid={`delete-${card.code}`}
            >
              <Trash2 size={11} strokeWidth={1.5} />
              Supprimer ce bon
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PriceRow({ label, value, subtle }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className={subtle ? "text-refuge-soft text-[12px] italic" : "text-refuge-text text-[13px]"}>
        {label}
      </span>
      <span className={`tabular-nums ${subtle ? "text-refuge-soft text-[12px]" : "text-refuge-text text-[13px]"}`}>
        {value}€
      </span>
    </div>
  );
}

function ActionBtn({ icon, label, onClick, primary, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={
        "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-body transition-all " +
        (primary
          ? "bg-refuge-ruby text-white hover:bg-refuge-ruby/90"
          : "bg-white text-refuge-text border border-refuge-border hover:border-refuge-ruby hover:text-refuge-ruby")
      }
    >
      {icon}
      {label}
    </button>
  );
}

function Field({ label, children, wide }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <p className="font-body text-[9px] tracking-[0.22em] uppercase text-refuge-soft mb-0.5">
        {label}
      </p>
      <div className="text-refuge-text">{children}</div>
    </div>
  );
}
